import type { IPermissionRepository } from '@src/domain/rbac/repository/permission_repository';
import type { Permission } from '@src/domain/rbac/entities/permission.entity';
import type { UpdatePermissionData } from '@src/domain/rbac/value_objects/update_permission_data';
import type { ApiClient } from '../api/client';
import { PermissionMapper } from '@src/application/rbac/mappers/permission.mapper';
import type { PermissionDTO, UpdatePermissionDTO } from '@src/application/rbac/dto';

/**
 * Permission Repository Implementation
 * Implements the IPermissionRepository interface using HTTP API calls
 */
export class PermissionRepositoryImpl implements IPermissionRepository {
    private readonly apiClient: ApiClient;

    constructor(apiClient: ApiClient) {
        this.apiClient = apiClient;
    }

    async listAll(): Promise<Permission[]> {
        const data = await this.apiClient.get<PermissionDTO[]>('/rbac/permissions');
        return PermissionMapper.toDomainList(data);
    }

    async getById(permissionId: string): Promise<Permission | null> {
        try {
            const data = await this.apiClient.get<PermissionDTO>(
                `/rbac/permissions/${permissionId}`
            );
            return PermissionMapper.toDomain(data);
        } catch (error) {
            // If 404, return null
            if ((error as any).status === 404) {
                return null;
            }
            throw error;
        }
    }

    async update(permissionId: string, data: UpdatePermissionData): Promise<Permission> {
        const requestData: UpdatePermissionDTO = {
            description: data.description,
        };

        const response = await this.apiClient.put<PermissionDTO>(
            `/rbac/permissions/${permissionId}`,
            requestData
        );
        return PermissionMapper.toDomain(response);
    }
}
